"use client";

/**
 * TopLeadsDisplay - Top leads and conversion metrics
 */

import { useState } from "react";
import { Avatar, Tag, Card, Progress } from "@/ui";
import { colors, spacing, radius, textStyles, typography, primitives } from "@/ui/tokens";
import {
  Leaderboard,
  Star,
  Work,
  Email,
  TrendingFlat,
} from "@mui/icons-material";
import {
  ToolCard,
  ToolHeader,
  ToolBody,
  ToolProgress,
  ToolError,
  ToolEmptyState,
} from "../../Tool";
import { deriveToolState } from "@/lib/sola/types/tools";
import type { ToolDisplayProps } from "@/lib/sola/types/tools";
import { showAllButtonClass } from "../utils";

interface Lead {
  contact: {
    id: number;
    name: string;
    first_name?: string;
    last_name?: string;
    email?: string;
    phone?: string;
    company?: string;
    job_title?: string;
    linkedin_url?: string;
    tags?: Array<{ id: number; name: string }>;
  };
  float: {
    id: number;
    uuid: string;
    name: string;
    heading?: string;
    share_link?: string;
  };
  recruiter_name?: string;
  stats: {
    click_count: number;
    view_count: number;
    total_view_duration: number;
    latest_interaction_at: string;
  };
  interactions?: Array<unknown>;
}

interface LeadsOutput {
  status?: string;
  leads?: Lead[];
  count?: number;
  filters?: {
    includeTeam: boolean;
    perPage: number;
    days: number;
  };
  message?: string;
  error?: string;
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
}

function LeadCard({ lead, rank }: { lead: Lead; rank: number }) {
  const { contact, float: floatData, stats } = lead;
  const engagementScore = (stats.click_count * 10) + (stats.view_count * 3) + Math.min(stats.total_view_duration, 100);
  const maxScore = 100;
  const scorePercentage = Math.min((engagementScore / maxScore) * 100, 100);

  return (
    <Card
      variant="outlined"
      padding="sm"
      style={{
        borderRadius: radius.sm,
        transition: "all 0.2s",
      }}
    >
      <div style={{ display: "flex", gap: spacing[4], alignItems: "flex-start" }}>
        {/* Rank Badge */}
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: rank <= 3 ? colors.signal.warning : colors.action.productLight,
            color: rank <= 3 ? colors.text.inverse : colors.text.muted,
            fontWeight: 600,
            fontSize: typography.scale.sm[0],
            flexShrink: 0,
          }}
        >
          {rank}
        </div>

        {/* Avatar */}
        <Avatar
          name={contact.name}
          size="lg"
        />

        {/* Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: spacing[2] }}>
            <span style={{ ...textStyles.body2, fontWeight: 600 }}>
              {contact.name}
            </span>
            {rank <= 3 && <Star style={{ fontSize: 16, color: colors.signal.warning }} />}
          </div>

          {(contact.job_title || contact.company) && (
            <span style={{ ...textStyles.caption, color: colors.text.muted, display: "block" }}>
              {contact.job_title}{contact.job_title && contact.company ? " at " : ""}{contact.company}
            </span>
          )}

          {/* Float name */}
          {floatData?.name && (
            <span style={{ ...textStyles.caption, color: colors.text.muted, display: "block", marginTop: spacing[1] }}>
              <Work style={{ fontSize: 12, marginRight: spacing[1], verticalAlign: "middle" }} />
              {floatData.name}
            </span>
          )}

          {/* Engagement Score */}
          <div style={{ marginTop: spacing[3] }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: spacing[1] }}>
              <span style={{ ...textStyles.caption, color: colors.text.muted }}>
                Engagement
              </span>
              <span style={{ ...textStyles.caption, fontWeight: 600 }}>
                {stats.click_count > 0 ? "High Intent" : stats.view_count > 2 ? "Interested" : "Viewed"}
              </span>
            </div>
            <Progress
              value={Math.min(scorePercentage, 100)}
              size="sm"
              color={
                scorePercentage >= 80 ? "success" :
                scorePercentage >= 50 ? "warning" : "danger"
              }
            />
          </div>

          {/* Metrics */}
          <div style={{ display: "flex", gap: spacing[4], marginTop: spacing[3] }}>
            {stats.click_count > 0 && (
              <span style={{ ...textStyles.caption, color: colors.text.muted }}>
                {stats.click_count} {stats.click_count === 1 ? "click" : "clicks"}
              </span>
            )}
            <span style={{ ...textStyles.caption, color: colors.text.muted }}>
              {stats.view_count} {stats.view_count === 1 ? "view" : "views"}
            </span>
            {stats.total_view_duration > 0 && (
              <span style={{ ...textStyles.caption, color: colors.text.muted }}>
                {formatDuration(stats.total_view_duration)} viewed
              </span>
            )}
          </div>

          {/* Tags */}
          <div style={{ display: "flex", gap: spacing[1], marginTop: spacing[2], flexWrap: "wrap" }}>
            {stats.click_count > 0 && (
              <Tag
                size="sm"
                variant="signal"
                color="danger"
                style={{ height: 20 }}
              >
                Clicked
              </Tag>
            )}
            {contact.email && (
              <Tag
                size="sm"
                variant="default"
                icon={<Email style={{ fontSize: 12 }} />}
                style={{ height: 20 }}
              >
                {contact.email}
              </Tag>
            )}
            {contact.tags?.map((tag) => (
              <Tag
                key={tag.id}
                size="sm"
                variant="default"
                style={{ height: 20 }}
              >
                {tag.name}
              </Tag>
            ))}
          </div>
        </div>
      </div>
    </Card>
  );
}

const MAX_VISIBLE = 8;

export function TopLeadsDisplay({ toolCall, isStreaming }: ToolDisplayProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const toolState = deriveToolState(toolCall);
  const { status, isLoading, isError, error } = toolState;

  const result = toolCall.finalOutput as LeadsOutput | undefined;
  const leads = result?.leads || [];
  const visibleLeads = showAll ? leads : leads.slice(0, MAX_VISIBLE);
  const needsTruncation = leads.length > MAX_VISIBLE;

  return (
    <ToolCard status={status} noPadding>
      <ToolHeader
        title={`Top Leads${result?.filters?.days ? ` - Past ${result.filters.days} days` : ""}`}
        toolName={toolCall.toolName}
        status={status}
        isExpanded={isExpanded}
        onToggleExpand={() => setIsExpanded((prev) => !prev)}
        icon={<Leaderboard style={{ fontSize: 18 }} />}
      />

      <ToolBody isOpen={isExpanded} noPadding>
        {isLoading && (
          <div style={{ padding: spacing[5], textAlign: "center" }}>
            <ToolProgress variant="dots" label="Loading top leads..." />
          </div>
        )}

        {isError && error && (
          <div style={{ padding: spacing[4] }}>
            <ToolError error={error} />
          </div>
        )}

        {!isLoading && (
          <>
            {/* Header */}
            <div
              style={{
                paddingLeft: spacing[5],
                paddingRight: spacing[5],
                paddingTop: spacing[3],
                paddingBottom: spacing[3],
                backgroundColor: colors.action.productLight,
                borderBottom: `1px solid ${colors.border.subtle}`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span style={{ ...textStyles.body2, fontWeight: 500 }}>
                {leads.length} {leads.length === 1 ? "lead" : "leads"}
                {result?.count && result.count > leads.length
                  ? ` of ${result.count}`
                  : ""}
              </span>
              {result?.filters?.days && (
                <Tag size="sm" variant="default" color="primary">
                  {result.filters.days} days
                </Tag>
              )}
            </div>

            {/* Leads List */}
            {leads.length > 0 ? (
              <div style={{ padding: spacing[4] }}>
                <div style={{ display: "flex", flexDirection: "column", gap: spacing[3] }}>
                  {visibleLeads.map((lead, index) => (
                    <LeadCard key={lead.contact?.id || index} lead={lead} rank={index + 1} />
                  ))}
                </div>
                {needsTruncation && !showAll && (
                  <button onClick={() => setShowAll(true)} className={showAllButtonClass}>
                    Show all {leads.length} leads
                  </button>
                )}
              </div>
            ) : (
              <ToolEmptyState icon={<TrendingFlat />} message={result?.message || "No leads found"} />
            )}
          </>
        )}
      </ToolBody>
    </ToolCard>
  );
}

export default TopLeadsDisplay;
